import React, { useEffect, useState } from 'react';

/**
 * Floating animation overlay for tip/gift events during a video call.
 * Consumes an events array (from CallEventsProvider) and renders each event
 * briefly, then removes it. Key'd by event.id so the same event is never
 * animated twice (idempotent vs the polling stream).
 */

// How long each animation stays on screen before fading out
const LIFE_MS_TIP = 2500;
const LIFE_MS_GIFT = 3200;

const animationKeyframes = `
  @keyframes vc-tip-float {
    0%   { transform: translate(-50%, 0) scale(0.6); opacity: 0; }
    15%  { transform: translate(-50%, -20px) scale(1.1); opacity: 1; }
    40%  { transform: translate(-50%, -60px) scale(1); opacity: 1; }
    100% { transform: translate(-50%, -160px) scale(0.8); opacity: 0; }
  }
  @keyframes vc-gift-float {
    0%   { transform: translate(-50%, 50px) scale(0.3) rotate(-12deg); opacity: 0; }
    15%  { transform: translate(-50%, -20px) scale(1.3) rotate(0deg); opacity: 1; }
    55%  { transform: translate(-50%, -90px) scale(1.1) rotate(4deg); opacity: 1; }
    100% { transform: translate(-50%, -220px) scale(0.9) rotate(-6deg); opacity: 0; }
  }
`;

const TipBubble = ({ event }) => (
  <div
    className="absolute bottom-24 left-1/2 pointer-events-none flex items-center gap-2 px-4 py-2 rounded-full shadow-xl whitespace-nowrap"
    style={{
      background: 'linear-gradient(135deg, var(--color-primary-500), var(--color-primary-700))',
      color: 'white',
      animation: `vc-tip-float ${LIFE_MS_TIP}ms ease-out forwards`,
    }}
  >
    <span className="text-lg">💰</span>
    <div className="text-left">
      <div className="text-[11px] opacity-80">
        {event.sender?.display_name || event.sender?.username}
      </div>
      <div className="text-sm font-bold">+${Number(event.amount).toFixed(2)}</div>
    </div>
  </div>
);

const GiftBubble = ({ event }) => (
  <div
    className="absolute bottom-24 left-1/2 pointer-events-none flex flex-col items-center"
    style={{
      animation: `vc-gift-float ${LIFE_MS_GIFT}ms ease-out forwards`,
    }}
  >
    <div className="mb-1">
      {event.gift?.image_url ? (
        <img src={event.gift.image_url} alt={event.gift?.name} className="w-20 h-20 object-contain drop-shadow-2xl" />
      ) : (
        <span className="text-6xl drop-shadow-2xl">{event.gift?.emoji || '🎁'}</span>
      )}
    </div>
    <div
      className="px-3 py-1 rounded-full text-xs font-semibold text-white shadow-lg whitespace-nowrap"
      style={{ background: 'linear-gradient(135deg, var(--color-primary-500), var(--color-primary-700))' }}
    >
      {event.sender?.display_name || event.sender?.username} · {event.gift?.name}
    </div>
  </div>
);

const CallEventOverlay = ({ events = [] }) => {
  const [visible, setVisible] = useState([]);

  useEffect(() => {
    if (!events.length) return;

    // Any event id we haven't shown yet, show it and schedule removal
    setVisible((prev) => {
      const existingIds = new Set(prev.map((e) => e.id));
      const newOnes = events.filter((e) => !existingIds.has(e.id));
      if (newOnes.length === 0) return prev;
      return [...prev, ...newOnes];
    });
  }, [events]);

  useEffect(() => {
    if (!visible.length) return;
    const timers = visible.map((e) => {
      const ttl = e.type === 'gift' ? LIFE_MS_GIFT : LIFE_MS_TIP;
      return setTimeout(() => {
        setVisible((prev) => prev.filter((v) => v.id !== e.id));
      }, ttl);
    });
    return () => timers.forEach(clearTimeout);
  }, [visible]);

  if (visible.length === 0) return null;

  return (
    <div className="absolute inset-0 pointer-events-none z-30">
      <style>{animationKeyframes}</style>
      {visible.map((e) =>
        e.type === 'gift'
          ? <GiftBubble key={e.id} event={e} />
          : <TipBubble key={e.id} event={e} />
      )}
    </div>
  );
};

export default CallEventOverlay;
